/**
 * Proofing Service API Client Setup
 *
 * This file configures global error handling for the auto-generated client
 * with automatic token refresh on authentication failures.
 *
 * Token Refresh Flow:
 * 1. Request fails with 401 error indicating token expiration
 * 2. Interceptor detects refreshable auth error
 * 3. Token refresh is initiated (or request is queued if refresh in progress)
 * 4. After successful refresh, original request and all queued requests are retried
 * 5. If refresh fails or retry limit exceeded, user is redirected to login
 *
 * Loop Prevention:
 * - Maximum 1 retry attempt per request (MAX_RETRY_ATTEMPTS = 1)
 * - Per-request tracking using WeakMap (automatically garbage collected)
 * - Immediate redirect if retry fails with 401
 *
 * Base URL and credentials are configured via createClientConfig in proofingService-client-config.ts.
 * DO NOT modify client.gen.ts directly as it will be overwritten.
 * Use the SDK functions which automatically use this configured client.
 */

import {
  handleAuthError,
  isRefreshableAuthError,
  isTokenExpired,
} from "@/lib/auth/authErrorHandler";
import { requestQueue } from "@/lib/auth/requestQueue";
import { tokenRefreshService } from "@/lib/auth/tokenRefreshService";
import type { HeyApiError } from "@/lib/types";
import type { RequestOptions } from "../../proofingService-client/client/types.gen";
import { client } from "../../proofingService-client/client.gen";

/**
 * Retry a failed request after successful token refresh
 *
 * This function re-executes the original request with the same parameters.
 * The refreshed authentication token will be included automatically via HTTP-only cookies.
 *
 * Key Features:
 * - **Body Cloning**: Clones request before accessing body to prevent stream consumption
 * - **Response Style Handling**: Supports "data", "fields", and fallback formats
 * - **Loop Prevention**: Redirects immediately if retry fails with 401
 *
 * Technical Details:
 * - Request bodies can only be read once (stream consumption)
 * - Cloning preserves body for POST/PUT/PATCH retries
 * - Response format matches original request's opts.responseStyle
 *
 * @param request - The original Request object to retry
 * @param opts - The original request options from hey-api client (includes responseStyle)
 * @returns Promise that resolves with the response data (format depends on opts.responseStyle)
 * @throws Re-throws any errors from the retry attempt
 */
async function retryRequest(request: Request, opts: RequestOptions): Promise<unknown> {
  console.log(`[Token Refresh] Retrying request: ${request.method} ${request.url}`);

  try {
    // Clone request to preserve body stream
    const clonedRequest = request.clone();

    // Re-execute the request with the same parameters
    // The refreshed token should now be in the HTTP-only cookie
    const response = await fetch(clonedRequest.url, {
      method: clonedRequest.method,
      headers: clonedRequest.headers,
      body:
        clonedRequest.method !== "GET" && clonedRequest.method !== "HEAD"
          ? clonedRequest.body
          : undefined,
      credentials: "include",
    });

    if (!response.ok) {
      const textError = await response.text();
      let jsonError: unknown;
      try {
        jsonError = JSON.parse(textError);
      } catch {
        // noop
      }
      const retryError = jsonError ?? textError;
      console.error(`[Token Refresh] Retry failed with status ${response.status}`, retryError);

      // If retry fails with 401, prevent infinite loops - redirect immediately
      if (response.status === 401) {
        console.warn("[Token Refresh] Retry failed with 401, redirecting to login");
        handleAuthError(retryError as HeyApiError);
      }

      throw retryError;
    }

    // Parse response based on content type
    const contentType = response.headers.get("content-type");
    const responseStyle = opts.responseStyle ?? "data";

    if (contentType?.includes("application/json")) {
      const data = await response.json();
      console.log(`[Token Refresh] Retry succeeded for ${request.method} ${request.url}`);

      if (responseStyle === "data") return data;
      if (responseStyle === "fields") return { data, response };
      return { data, response }; // Fallback: object with both
    }

    const text = await response.text();
    console.log(`[Token Refresh] Retry succeeded for ${request.method} ${request.url}`);

    if (responseStyle === "data") return text;
    if (responseStyle === "fields") return { data: text, response };
    return { data: text, response }; // Fallback: object with both
  } catch (retryError) {
    console.error("[Token Refresh] Retry exception:", retryError);
    throw retryError;
  }
}

/**
 * Track retry attempts per request to prevent infinite loops
 *
 * Uses WeakMap for automatic garbage collection when requests are no longer referenced.
 * This ensures no memory leaks from tracking request objects.
 */
const retryAttempts = new WeakMap<Request, number>();

/**
 * Maximum number of retry attempts per request
 *
 * Set to 1 to allow one retry after token refresh.
 * If the retry also fails with 401, the user is redirected to login immediately.
 */
const MAX_RETRY_ATTEMPTS = 1;

/**
 * Global error interceptor for handling 401 authentication errors with automatic token refresh
 *
 * This interceptor implements the following flow:
 * 1. Detects refreshable authentication errors (401 with token expiration)
 * 2. Prevents infinite loops by limiting retry attempts per request
 * 3. Coordinates token refresh across concurrent requests (uses request queue)
 * 4. Retries original request and all queued requests after successful refresh
 * 5. Redirects to login if refresh fails or retry limit exceeded
 *
 * Non-refreshable errors (invalid token, network errors, etc.) are passed through
 * or trigger immediate redirect as appropriate.
 */
client.interceptors.error.use(
  async (error: unknown, response: Response, request: Request, opts: RequestOptions) => {
    const startTime = performance.now();

    console.error("API Error:", error);
    console.log("Response Status:", response.status);
    console.log("[Token Refresh Debug] Error structure:", JSON.stringify(error, null, 2));
    console.log("[Token Refresh Debug] Error type:", typeof error);
    console.log(
      "[Token Refresh Debug] Error keys:",
      error && typeof error === "object" ? Object.keys(error) : "N/A",
    );

    // Check if this is a refreshable auth error
    if (isRefreshableAuthError(error, response)) {
      console.log("[Token Refresh] Detected refreshable auth error");

      // Prevent infinite retry loops
      const attempts = retryAttempts.get(request) || 0;
      if (attempts >= MAX_RETRY_ATTEMPTS) {
        console.warn(
          `[Token Refresh] Maximum retry attempts (${MAX_RETRY_ATTEMPTS}) reached, redirecting to login`,
        );
        handleAuthError(error as HeyApiError);
        throw error;
      }

      // Check if this is explicitly a token expiration (has codeText or expiration message)
      const isExpired = isTokenExpired(error);
      console.log(`[Token Refresh] Token expired: ${isExpired}`);

      if (isExpired) {
        // Check if a refresh is already in progress
        const isRefreshing = tokenRefreshService.getRefreshState();
        console.log(`[Token Refresh] Refresh in progress: ${isRefreshing}`);

        if (isRefreshing) {
          // Queue this request to be retried after the ongoing refresh completes
          const queueSize = requestQueue.size() + 1;
          console.log(
            `[Token Refresh] Queueing request: ${request.method} ${request.url} (queue size: ${queueSize})`,
          );
          return new Promise((resolve, reject) => {
            // Store request, fetch options, AND hey-api options for proper retry
            // - request.clone().body: Preserves body stream for POST/PUT/PATCH
            // - opts: Preserves responseStyle for correct response parsing
            requestQueue
              .enqueue(
                request,
                {
                  method: request.method,
                  headers: request.headers,
                  body:
                    request.method !== "GET" && request.method !== "HEAD"
                      ? request.clone().body
                      : undefined,
                  credentials: "include",
                },
                opts, // Store hey-api RequestOptions for correct response parsing
              )
              .then(() => {
                // Reset retry counter for queued requests to allow one fresh attempt
                // Without this, queued requests would inherit the retry count and fail immediately
                retryAttempts.delete(request);
                return retryRequest(request, opts);
              })
              .then(resolve)
              .catch(reject);
          });
        }

        // Initiate token refresh
        console.log("[Token Refresh] Initiating token refresh");
        try {
          await tokenRefreshService.refreshToken();
          console.log("[Token Refresh] Token refresh successful");

          // Track retry attempt
          retryAttempts.set(request, attempts + 1);

          // Retry the original request
          const result = await retryRequest(request, opts);

          // Process queued requests
          const queuedRequests = requestQueue.getAll();
          console.log(`[Token Refresh] Processing ${queuedRequests.length} queued requests`);

          let successCount = 0;
          let failureCount = 0;

          for (const queuedRequest of queuedRequests) {
            try {
              // Use the original RequestOptions stored with each queued request
              // This ensures each request gets its proper response format (data/fields/etc)
              // Falls back to current opts if not stored (backward compatibility)
              const queuedOpts = (queuedRequest.opts ?? opts) as RequestOptions;
              const queuedResult = await retryRequest(queuedRequest.request, queuedOpts);
              queuedRequest.resolve(queuedResult as Response);
              successCount++;
            } catch (queuedError) {
              queuedRequest.reject(queuedError);
              failureCount++;
            }
          }

          // Clear the queue after processing
          requestQueue.clear();

          const totalDuration = Math.round(performance.now() - startTime);
          console.log(
            `[Token Refresh] ✓ Resolution complete: ${successCount} succeeded, ${failureCount} failed (total time: ${totalDuration}ms)`,
          );

          return result;
        } catch (refreshError) {
          console.error("[Token Refresh] Token refresh failed", refreshError);

          // Clear queue with error
          const queueError = new Error("Token refresh failed");
          const queueSize = requestQueue.size();
          requestQueue.clear(queueError);

          const totalDuration = Math.round(performance.now() - startTime);
          console.log(
            `[Token Refresh] ✗ Resolution failed: redirecting to login, ${queueSize} requests rejected (total time: ${totalDuration}ms)`,
          );

          // Redirect to login
          handleAuthError(error as HeyApiError);
          throw error;
        }
      } else {
        // Non-expired 401 error (invalid token, insufficient permissions, etc.)
        console.log("[Token Refresh] Non-refreshable 401 error, redirecting to login");
        handleAuthError(error as HeyApiError);
        throw error;
      }
    }

    // Not an auth error, pass through
    throw error;
  },
);

export const proofingClient = client;
